import React, { useEffect, useState } from "react"
import { Card, Col, Row, Input, Button } from "reactstrap"
import { useNavigate, useSearchParams } from "react-router-dom"
import UpdateTable from "../../../components/Common/UpdatedTable"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { listWorkforce, workforceStatus } from "../../../services/Hrms/workforce"
import { getTrades } from "../../../services/admin"
import { toastError, toastSuccess } from "../../../helpers/msg_helper"

const CATEGORIES = ["WORKER", "STAFF"]
const CAMP_OPTS = ["PARCO", "JEBEL ALI", "DIP", "STAFF", "VACATION", "OUTSIDE", "ABSCOND", "CLIENT", "CANCEL"]

const Workforce = () => {
  const navigate = useNavigate()
  const [urlParams] = useSearchParams()
  const companyname = localStorage.getItem("companyname")
  document.title = `Workforce${companyname ? ` | ${companyname}` : ""}`

  const [data, setData] = useState()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [size, setSize] = useState(25)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [trades, setTrades] = useState([])
  // Filters (seeded from the URL so HR-report drill-downs land here pre-filtered)
  const [filters, setFilters] = useState({
    trade: urlParams.get("trade") || "",
    nationality: urlParams.get("nationality") || "",
    category: urlParams.get("category") || "",
    camp: urlParams.get("camp") || "",
    company_id: urlParams.get("company_id") || "",
    status: urlParams.get("status") || "",
  })
  const setF = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1) }

  useEffect(() => {
    ;(async () => { try { const r = await getTrades(); setTrades((r?.data || []).filter(t => t.is_active)) } catch (e) { /* optional */ } })()
  }, [])

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const p = new URLSearchParams({ searchText: search, sizePerPage: size })
        Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v) })
        const r = await listWorkforce({ page, params: p.toString() })
        setData(r?.data)
      } catch (e) { toastError(e?.message || "Failed to load") }
      finally { setLoading(false) }
    })()
  }, [page, search, size, count, filters])

  const toggleStatus = async id => {
    try { await workforceStatus(id); toastSuccess("Status updated"); setCount(c => c + 1) }
    catch (e) { toastError(e?.message) }
  }

  const columns = [
    { label: "Employee Code", renderCell: i => <div>{i?.amj_code || "-"}</div> },
    { label: "Name", renderCell: i => <div className="capitalize-letter">{i?.name}</div> },
    { label: "Company", renderCell: i => <div title={i?.company?.company_name}>{i?.company?.code || i?.company?.company_name || "-"}</div> },
    { label: "Trade", renderCell: i => <div>{i?.hired_trade || "-"}</div> },
    { label: "Nationality", renderCell: i => <div>{i?.nationality || "-"}</div> },
    { label: "Category", renderCell: i => <div>{i?.category || "-"}</div> },
    {
      label: "Status",
      renderCell: i => (
        <div className={`font-13 ${i?.is_active ? "status-active" : "status-inactive"}`} role="button" onClick={() => toggleStatus(i?.id)}>
          {i?.is_active ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      label: "Action",
      renderCell: i => (
        <i className="uil-pen action-icon" role="button" title="Edit" style={{ cursor: "pointer", fontSize: 17 }} onClick={() => navigate(`/hrms-workforce/edit/${i?.id}`)} />
      ),
    },
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center">
            <Breadcrumbs title="HRMS" breadcrumbObject={[
              { title: "HRMS", link: "/hrms" },
              { title: "Workforce", link: "/hrms-workforce" },
            ]} />
            <Button color="primary" className="px-2 mb-3 me-2" onClick={() => navigate("/hrms-workforce/add")}>
              +&nbsp; Employee
            </Button>
          </div>
          <Card className="me-2 px-2 pb-2">
            <Row className="mt-2 mb-2 g-2 align-items-center">
              <Col md={3}>
                <Input
                  placeholder="Search name / code / trade / nationality"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1) }}
                />
              </Col>
              <Col xs="auto">
                <Input type="select" bsSize="sm" value={filters.trade} onChange={e => setF("trade", e.target.value)} style={{ minWidth: 150 }}>
                  <option value="">All trades</option>
                  {trades.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </Input>
              </Col>
              <Col xs="auto">
                <Input type="select" bsSize="sm" value={filters.category} onChange={e => setF("category", e.target.value)}>
                  <option value="">All categories</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Input>
              </Col>
              <Col xs="auto">
                <Input type="select" bsSize="sm" value={filters.camp} onChange={e => setF("camp", e.target.value)}>
                  <option value="">All camps</option>
                  {CAMP_OPTS.map(c => <option key={c} value={c}>{c}</option>)}
                </Input>
              </Col>
              <Col xs="auto">
                <Input type="select" bsSize="sm" value={filters.status} onChange={e => setF("status", e.target.value)}>
                  <option value="">All status</option>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </Input>
              </Col>
              {Object.values(filters).some(Boolean) && (
                <Col xs="auto">
                  <Button color="link" size="sm" className="p-0" onClick={() => setFilters({ trade: "", nationality: "", category: "", camp: "", company_id: "", status: "" })}>Clear filters</Button>
                </Col>
              )}
            </Row>
            {(filters.nationality || filters.company_id) && (
              <div className="mb-2 d-flex flex-wrap" style={{ gap: 6 }}>
                {filters.nationality && (
                  <span className="badge bg-light text-dark border" style={{ fontSize: 12, padding: "5px 9px" }}>
                    Nationality: {filters.nationality} <span role="button" className="ms-1" onClick={() => setF("nationality", "")}>✕</span>
                  </span>
                )}
                {filters.company_id && (
                  <span className="badge bg-light text-dark border" style={{ fontSize: 12, padding: "5px 9px" }}>
                    {data?.data?.[0]?.company?.company_name || "Company"} <span role="button" className="ms-1" onClick={() => setF("company_id", "")}>✕</span>
                  </span>
                )}
              </div>
            )}
            <hr className="table-divide" />
            <Row className="mt-2 ever-rise-table">
              <Col xs={12}>
                {data?.data && (
                  <UpdateTable
                    nodes={data?.data}
                    paginate={data}
                    CurrentPage={page}
                    columns={columns}
                    handlePagination={p => setPage(p)}
                    handleSizePerPage={s => { setSize(s); setPage(1) }}
                    tableWidth="12% 22% 22% 14% 12% 8% 5% 5%"
                    mobileWidth="30% 30% 30% 30% 20% 20% 20% 20%"
                    isLoading={loading}
                  />
                )}
              </Col>
            </Row>
          </Card>
        </div>
      </div>
    </React.Fragment>
  )
}

export default Workforce
